import { useMemo } from 'react'

import { ENTITY_TYPES } from '@sales-center/common'

import { useGetCustomFieldsSettingsOnMount } from '@user/store/customFieldSettings'
import { useGetEntityFormSettingsOnMount } from '@user/store/entityFormSettings'
import type { IEntityFormSetting } from '@user/store/entityFormSettings/typings'
import { useAppSelector } from '@user/store/hooks'

import { addLeadSectionLabels, addOppSectionLabels } from '../constants'
import { getFieldOrder } from '../config/fieldConfigs'
import {
  isBuiltinFieldSetting,
  isCustomFieldSetting,
  selectUnifiedFieldSettings,
} from './selectUnifiedFieldSettings'

/**
 * Loads and shapes every field setting for one entity type.
 *
 * Fetches the built-in and custom field settings on mount and exposes a single
 * combined loading flag for the page's loading indicator.
 *
 * Returns the built-in fields both flat (`settings`) and grouped by section
 * (`settingsBySection`, sorted into registry display order), plus the custom
 * fields separately, since the two render through different components.
 *
 * @param entityType Which entity's settings to load.
 */
export function useFieldSettingsData(entityType: ENTITY_TYPES) {
  const { loading } = useGetEntityFormSettingsOnMount(entityType)
  const { loading: loadingCustomSettings } =
    useGetCustomFieldsSettingsOnMount(entityType)
  const settingsLoading = loading && loadingCustomSettings

  const unifiedSettings = useAppSelector(selectUnifiedFieldSettings(entityType))

  const settings = useMemo(
    () => unifiedSettings.filter(isBuiltinFieldSetting),
    [unifiedSettings],
  )

  const customSettings = useMemo(
    () => unifiedSettings.filter(isCustomFieldSetting),
    [unifiedSettings],
  )

  const settingsBySection = useMemo(() => {
    const bySection: Record<string, IEntityFormSetting[]> = {}
    const settingsSorted = [...settings].sort(
      (a, b) =>
        getFieldOrder(entityType, a.fieldId) -
        getFieldOrder(entityType, b.fieldId),
    )
    Object.keys(
      entityType === ENTITY_TYPES.Lead
        ? addLeadSectionLabels
        : addOppSectionLabels,
    ).forEach((sectionId) => {
      bySection[sectionId] = settingsSorted.filter(
        (setting) => setting.sectionId === sectionId,
      )
    })
    return bySection
  }, [settings, entityType])

  return { settingsLoading, settings, settingsBySection, customSettings }
}
